import { useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
// renaming on import since the collapse becomes expand and vice versa when on the right-hand side of the screen for attachments panel
import { SidebarCollapseIcon } from 'foundations-assets/icons/components/SidebarCollapseIcon';
import { SidebarExpandIcon } from 'foundations-assets/icons/components/SidebarExpandIcon';
import styled from 'foundations-theming-specialty/utils';
import I18n from 'I18n';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { MessageAttachment } from './MessageAttachment';
import { useIsMobile } from '../hooks/useBreakpoint';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NoAttachments } from './NoAttachments';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useParams } from 'react-router-dom';
import { useThreadAttachments } from '../data/useThreadAttachments';
import { MAX_ATTACHMENTS_PER_THREAD } from '../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RotatedIcon = styled.span.withConfig({
  displayName: "AttachmentsPanel__RotatedIcon"
})(["display:contents;& > svg{transform:rotate(180deg);}"]);
const PanelWrapper = styled.div.withConfig({
  displayName: "AttachmentsPanel__PanelWrapper"
})(["display:flex;flex-direction:column;height:100%;background-color:", ";flex:", ";min-width:", ";justify-content:space-between;border-inline-start-width:1px;border-inline-start-style:solid;border-color:", ";color:", ";padding-inline:", ";padding-block:", ";transition:width 0.1s ease,flex 0.1s ease;"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  $isCollapsed
}) => $isCollapsed ? '0 0 80px' : '0 1 435px', ({
  $isCollapsed
}) => $isCollapsed ? '80px' : '200px', ({
  theme
}) => theme.color['border-core-disabled'], ({
  theme
}) => theme.color['text-core-default'], ({
  theme,
  $isCollapsed
}) => $isCollapsed ? theme.space['600'] : theme.space['800'], ({
  theme
}) => theme.space['400']);
const ContentWrapper = styled.div.withConfig({
  displayName: "AttachmentsPanel__ContentWrapper"
})(["display:flex;flex-direction:column;gap:", ";flex:1;min-width:0;opacity:", ";transition:opacity 0.3s ease;overflow:hidden;"], ({
  theme
}) => theme.space['500'], ({
  $isCollapsed
}) => $isCollapsed ? '0' : '1');
const AttachmentsPanelContent = ({
  attachments,
  isLoading
}) => {
  const loadedMaxAttachments = attachments.length === MAX_ATTACHMENTS_PER_THREAD;
  if (isLoading && attachments.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      alignItems: "center",
      justifyContent: "center",
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  return attachments.length > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [attachments.map(attachment => /*#__PURE__*/_jsx(MessageAttachment, {
      attachment: attachment.attachment,
      sensitiveAttachment: attachment.sensitiveAttachment,
      customSubtitle: attachment.sentDate ? I18n.moment(attachment.sentDate).format('LL') : null
    }, attachment.fileId)), loadedMaxAttachments && /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: "detail-200",
      textAlign: "center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachmentsPanel.displayingMaxAttachments",
        options: {
          MAX_ATTACHMENTS_PER_THREAD
        }
      })
    })]
  }) : /*#__PURE__*/_jsx(NoAttachments, {});
};
export function AttachmentsPanel({
  showAsOverlay,
  isAttachmentsPanelOpen,
  setIsAttachmentsPanelOpen
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const {
    ticketId,
    threadId
  } = useParams();
  const {
    data: threadAttachments,
    loading
  } = useThreadAttachments(Number(ticketId), threadId || '');
  const combinedAttachments = useMemo(() => {
    if (!threadAttachments) return [];
    const {
      attachmentsList,
      sensitiveAttachmentsList
    } = threadAttachments;
    return [...attachmentsList.map((attachment, i) => ({
      fileId: `attachment-${i}`,
      sensitiveAttachment: undefined,
      attachment,
      sentDate: attachment.attachmentTimestamp
    })), ...sensitiveAttachmentsList.map((sensitiveAttachment, i) => ({
      fileId: `sensitive-attachment-${i}`,
      sensitiveAttachment,
      attachment: undefined,
      sentDate: undefined
    }))];
  }, [threadAttachments]);

  // Overlay mode: render as slide-in Panel
  if (showAsOverlay) {
    if (!isAttachmentsPanelOpen) return null;
    return /*#__PURE__*/_jsxs(Panel, {
      align: "right",
      inlineSize: isMobile ? '100dvw' : '435px',
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        color: "icon-core-default",
        onClick: () => setIsAttachmentsPanelOpen(false),
        "aria-label": I18n.text('customer-portal-app.attachmentsPanel.ariaClose')
      }), /*#__PURE__*/_jsxs(PanelSection, {
        flex: "1",
        display: "flex",
        flexDirection: "column",
        paddingBlock: "600",
        gap: "400",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h2",
          color: "text-core-default",
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.attachmentsPanel.title"
          })
        }), /*#__PURE__*/_jsx(AttachmentsPanelContent, {
          attachments: combinedAttachments,
          isLoading: loading
        })]
      })]
    });
  }

  // Non overlay mode: render with collapsible sidebar
  return /*#__PURE__*/_jsxs(PanelWrapper, {
    $isCollapsed: isCollapsed,
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: isCollapsed ? 'center' : 'start',
      alignItems: "center",
      gap: "400",
      marginBlockEnd: "400",
      children: [/*#__PURE__*/_jsx(IconButton
      // @ts-expect-error IconButton color prop is not currently allowed
      , {
        color: "icon-core-default",
        onClick: () => setIsCollapsed(!isCollapsed),
        "aria-label": isCollapsed ? I18n.text('customer-portal-app.attachmentsPanel.expand') : I18n.text('customer-portal-app.attachmentsPanel.collapse'),
        use: "unstyled",
        children: /*#__PURE__*/_jsx(RotatedIcon, {
          children: isCollapsed ? /*#__PURE__*/_jsx(SidebarExpandIcon, {
            size: "800"
          }) : /*#__PURE__*/_jsx(SidebarCollapseIcon, {
            size: "800"
          })
        })
      }), !isCollapsed && /*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-core-default",
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.attachmentsPanel.title"
        })
      })]
    }), /*#__PURE__*/_jsx(ContentWrapper, {
      $isCollapsed: isCollapsed,
      children: !isCollapsed && /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "400",
        flex: "1",
        inlineSize: "100%",
        maxInlineSize: "100%",
        minInlineSize: "0",
        backgroundColor: "fill-surface-default",
        overflow: "auto",
        children: /*#__PURE__*/_jsx(AttachmentsPanelContent, {
          attachments: combinedAttachments,
          isLoading: loading
        })
      })
    })]
  });
}